// pages/About.jsx
import React from 'react';
import Layout from '../components/layout/Layout';
import TeamSection from '../components/about/TeamSection';
import { motion } from 'framer-motion';

const About = () => {
  const values = [
    { icon: '✨', title: 'Excellence', description: 'Striving for the highest standards in dental care and patient service', gradient: 'from-blue-500 to-cyan-400' },
    { icon: '🤝', title: 'Integrity', description: 'Maintaining honest and ethical practices in all our interactions', gradient: 'from-blue-600 to-indigo-500' },
    { icon: '💕', title: 'Compassion', description: 'Providing care with empathy and understanding for all patients', gradient: 'from-blue-500 to-sky-400' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <div className="hero-gradient text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl font-extrabold sm:text-5xl md:text-6xl">
              About Zia Dental
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-blue-100">
              Dedicated to providing exceptional dental care with a gentle touch
            </p>
          </motion.div>
        </div>
      </div>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: 'Our Mission', text: 'To provide comprehensive, high-quality dental care in a comfortable and welcoming environment. We are committed to improving the oral health of our community through education, prevention, and personalized treatment plans.' },
              { title: 'Our Vision', text: 'To be the leading dental care provider in our community, known for excellence in patient care, innovative treatments, and a commitment to continuous improvement in dental healthcare delivery.' },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-gray-50 p-8 rounded-xl border-l-4 border-blue-500 hover:shadow-lg transition-shadow duration-300"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{card.title}</h2>
                <p className="text-gray-500 leading-relaxed">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Our History</h2>
            <div className="prose max-w-none space-y-4">
              <p className="text-gray-500 leading-relaxed">
                Founded in 1998, Zia Dental has grown from a small practice to a comprehensive dental care center.
                Over the years, we have invested in the latest dental technology and continued education for our staff
                to ensure we provide the highest standard of care to our patients.
              </p>
              <p className="text-gray-500 leading-relaxed">
                Our commitment to excellence has earned us the trust of thousands of patients and numerous accolades
                in the dental healthcare community. We take pride in our patient-first approach and our role in
                promoting oral health in our community.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-gray-900 text-center mb-14"
          >
            Our Values
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="text-center group"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <span className="text-2xl">{value.icon}</span>
                </div>
                <h3 className="mt-5 text-xl font-bold text-gray-900">{value.title}</h3>
                <p className="mt-2 text-gray-500 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <TeamSection />
    </Layout>
  );
};

export default About;